import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
import percentIcon from '@src/assets/icons/percentIcon.svg';
import { map, max, min } from 'lodash';
import { delimitNumber } from '@src/utils/number';
import Tooltip from '@src/components/Tooltip';
import { checkVietNam } from '@src/utils/checkCountry';
import { useSelector } from 'react-redux';
import Slider from '@mui/material/Slider';
import { StyledBuyMoreCharacters } from './index.style';

const BuyExtracharacters = memo(
  ({
    calculateCharacterPurchase,
    currentNumberCharacters,
    onChangeNumberCharacters,
    packageCode,
    packages,
  }) => {
    // Mốc giảm giá tiền khi mua nhiều ký tự, mặc định 3000000
    const CREDIT_VOUCHER = 3000000;
    const { t, i18n } = useTranslation();
    const { language } = i18n;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { package: packageState } = useSelector((state) => state.order);
    const isVietNam = checkVietNam();
    const muiSliderMarkNumber = isMobile ? 17 : 19;
    const marks = useMemo(
      () =>
        map(packageState?.creditPurchaseOptions || [], (value) => ({ value })),
      [packageState],
    );

    const maxNumberCharacter = useMemo(
      () => max(packageState?.creditPurchaseOptions || []),
      [packageState],
    );

    const minNumberCharacter = useMemo(
      () => min(packageState?.creditPurchaseOptions || []) || 0,
      [packageState],
    );

    const ValueLabelComponent = (props) => {
      const { children, value } = props;
      return (
        <Tooltip
          enterTouchDelay={0}
          placement="top"
          arrow
          title={value?.toLocaleString(language)}
        >
          {children}
        </Tooltip>
      );
    };

    const packageTopUpPackageCode = useMemo(
      () => packages?.find((pkg) => pkg?.topUpPackageCode === packageCode),
      [packages, packageCode],
    );
    return (
      <StyledBuyMoreCharacters>
        <div className="head">
          <span className="title">{t('buyMoreCharacters')}</span>
          {packageTopUpPackageCode && (
            <Typography
              className="product-name"
              component="span"
              dangerouslySetInnerHTML={{
                __html: t('packageNameByLevel', {
                  name: packageTopUpPackageCode?.name?.[language],
                }),
              }}
            />
          )}
        </div>
        <div className="price-per-number-character">
          *
          {isVietNam ? (
            <span className="discount-unit-price-small underline">đ</span>
          ) : (
            <span className="discount-unit-price-small">$</span>
          )}
          {isVietNam
            ? packageState?.vndPricePerUnit?.toLocaleString(language)
            : packageState?.usdPricePerUnit?.toLocaleString(language)}
          /{packageState?.creditsPerUnit?.toLocaleString(language)}{' '}
          {t('charactersLower')}
        </div>
        <div className="characters-slider">
          <div className="number-price">
            <span className="title-number-price">{t('characterNumber')}</span>
            <div className="number-characters">
              <span className="number-character">
                {t('remainChars', {
                  numberCharacters: delimitNumber(
                    currentNumberCharacters,
                    language,
                  ),
                })}
              </span>
              {!!calculateCharacterPurchase?.discountRate && (
                <div className="tag-discount">
                  <img
                    src={percentIcon}
                    className="percent-icon"
                    alt="percentIcon"
                  />
                  {t('percentDiscount', {
                    percentDiscount: Math.floor(
                      calculateCharacterPurchase?.discountRate * 100,
                    ),
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="info-character">
            {t('numberCharactersWillAdded', {
              numberCharacters:
                currentNumberCharacters.toLocaleString(language),
            })}
          </div>
        </div>
        <div
          data-id="slider-character-amount"
          data-character-amount={currentNumberCharacters}
          className="slider"
        >
          <Slider
            className="slider-mui"
            sx={{
              height: 12,
              '& .MuiSlider-track': {
                backgroundColor: 'gray',
                height: 12,
                color: '#007AFF',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#ccc',
                height: 12,
              },
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
                color: '#fff',
                background: '#fff',
                zIndex: 200,
                boxShadow: '0px 3px 8px 0px #0000003D',
              },
              '& .MuiSlider-thumb:before': {
                width: 12,
                height: 12,
                background: '#007AFF',
                zIndex: 201,
              },
              '& .MuiSlider-mark': {
                backgroundColor: 'transparent',
              },
              [`& .MuiSlider-mark:nth-child(${muiSliderMarkNumber})`]:
                currentNumberCharacters !== CREDIT_VOUCHER && {
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  marginLeft: 2,
                },
              '& .MuiSlider-thumb.Mui-focusVisible': {
                boxShadow: 'none',
              },
            }}
            components={{
              ValueLabel: ValueLabelComponent,
            }}
            disableSwap={!!isMobile}
            step={null}
            marks={marks}
            min={minNumberCharacter}
            max={maxNumberCharacter}
            value={currentNumberCharacters}
            onChange={(e) => {
              onChangeNumberCharacters(e.target.value);
            }}
            valueLabelDisplay="auto"
            getAriaValueText={() => null}
          />
        </div>
        {/* <div className="cal-price">
          <span className="title-number-price">{t('priceEstimate')}</span>
          {viewPriceEstimate}
        </div>
        {viewSavePercent} */}
      </StyledBuyMoreCharacters>
    );
  },
);

export default BuyExtracharacters;
